import { useState, useEffect, useCallback, useRef } from "react";
import * as SurveyAPI from "../../services/surveyService";

const DEBOUNCE_MS = 500;
const BACKUP_KEY = (id) => `survey_backup_${id}`;

export function useSurvey(surveyCode, { sourceContext = "dashboard", locale, metadata } = {}) {
  const [survey, setSurvey] = useState(null);
  const [responseId, setResponseId] = useState(null);
  const [answers, setAnswers] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [unlockInfo, setUnlockInfo] = useState(null);
  const [error, setError] = useState(null);
  const timerRef = useRef(null);
  const pendingRef = useRef([]);
  const flushRef = useRef(null);

  useEffect(() => {
    if (!surveyCode) return;
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        const surveyData = await SurveyAPI.fetchSurvey(surveyCode, locale);
        if (cancelled) return;
        setSurvey(surveyData);

        const resp = await SurveyAPI.startOrResumeResponse(surveyCode, {
          sourceContext,
          locale,
          metadata,
        });
        if (cancelled) return;

        setResponseId(resp.response_id);

        if (resp.status === "already_completed") {
          setCompleted(true);
          return;
        }

        const savedAnswers = resp.saved_answers || {};
        const backup = localStorage.getItem(BACKUP_KEY(resp.response_id));
        let merged = { ...savedAnswers };
        if (backup) {
          try {
            const parsed = JSON.parse(backup);
            merged = { ...merged, ...parsed };
          } catch (_) {}
        }
        setAnswers(merged);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [surveyCode, sourceContext, locale, metadata]);

  const setAnswer = useCallback((questionCode, value) => {
    setAnswers((prev) => {
      const next = { ...prev, [questionCode]: value };
      if (responseId) {
        localStorage.setItem(BACKUP_KEY(responseId), JSON.stringify(next));
      }
      return next;
    });
    setErrors((prev) => {
      const next = { ...prev };
      delete next[questionCode];
      return next;
    });

    pendingRef.current.push(questionCode);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => flushRef.current?.(), DEBOUNCE_MS);
  }, [responseId]);

  const flushAnswers = useCallback(async () => {
    if (!responseId || pendingRef.current.length === 0) return;

    const codes = [...new Set(pendingRef.current)];
    pendingRef.current = [];

    if (!survey) return;
    const qLookup = {};
    for (const q of survey.questions || []) qLookup[q.code] = q;

    const batch = codes
      .filter((c) => answers[c] !== undefined && qLookup[c])
      .map((c) => ({
        question_code: c,
        question_id: qLookup[c].id,
        value: answers[c],
      }));

    if (batch.length === 0) return;

    try {
      setSaving(true);
      await SurveyAPI.saveAnswers(responseId, batch);
    } catch (err) {
      if (err.message === "validation" && err.details) {
        const newErrors = {};
        for (const d of err.details) newErrors[d.question_code] = d.error;
        setErrors((prev) => ({ ...prev, ...newErrors }));
      }
    } finally {
      setSaving(false);
    }
  }, [responseId, survey, answers]);

  flushRef.current = flushAnswers;

  const answersRef = useRef(answers);
  answersRef.current = answers;

  const submit = useCallback(async () => {
    if (!responseId || !survey) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    pendingRef.current = [];

    const currentAnswers = answersRef.current;
    const qLookup = {};
    for (const q of survey.questions || []) qLookup[q.code] = q;

    const batch = Object.keys(currentAnswers)
      .filter((c) => currentAnswers[c] !== undefined && qLookup[c])
      .map((c) => ({
        question_code: c,
        question_id: qLookup[c].id,
        value: currentAnswers[c],
      }));

    if (batch.length > 0) {
      try {
        setSaving(true);
        await SurveyAPI.saveAnswers(responseId, batch);
      } catch (err) {
        if (err.message === "validation" && err.details) {
          const newErrors = {};
          for (const d of err.details) newErrors[d.question_code] = d.error;
          setErrors((prev) => ({ ...prev, ...newErrors }));
        } else {
          setError(err.message);
        }
        setSaving(false);
        return;
      } finally {
        setSaving(false);
      }
    }

    try {
      setSubmitting(true);
      const result = await SurveyAPI.completeResponse(responseId);
      if (result.unlock) setUnlockInfo(result.unlock);
      setCompleted(true);
      localStorage.removeItem(BACKUP_KEY(responseId));
    } catch (err) {
      if (err.message === "missing_required" && err.missingCodes) {
        const newErrors = {};
        for (const code of err.missingCodes) newErrors[code] = "required";
        setErrors((prev) => ({ ...prev, ...newErrors }));
      } else {
        setError(err.message);
      }
    } finally {
      setSubmitting(false);
    }
  }, [responseId, survey]);

  const abandon = useCallback(async () => {
    if (!responseId) return;
    try {
      await SurveyAPI.abandonResponse(responseId);
      localStorage.removeItem(BACKUP_KEY(responseId));
    } catch (_) {}
  }, [responseId]);

  const visibleQuestions = survey?.questions?.filter((q) => {
    if (!q.branching?.show_if) return true;
    const { question_code, in: allowed } = q.branching.show_if;
    const val = answers[question_code];
    if (!val) return false;
    const selected = val.selected ?? val.value ?? val.text ?? null;
    if (Array.isArray(selected)) return selected.some((s) => allowed.includes(s));
    return allowed.includes(String(selected));
  }) ?? [];

  const requiredCount = visibleQuestions.filter(
    (q) => q.validation?.required
  ).length;
  const answeredRequired = visibleQuestions.filter(
    (q) => q.validation?.required && answers[q.code] !== undefined
  ).length;
  const progressPct = requiredCount > 0 ? Math.round((answeredRequired / requiredCount) * 100) : 0;

  return {
    survey,
    responseId,
    answers,
    errors,
    loading,
    saving,
    submitting,
    completed,
    unlockInfo,
    error,
    visibleQuestions,
    progressPct,
    setAnswer,
    submit,
    abandon,
    flushAnswers,
  };
}
